import { ChatMessage, ChatMessageLocale } from './interfaces';
import { ChatMessageComponent } from './ChatMessageComponent';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useGetFhirPatientByCodexIdLazyQuery,
  useGetFhirProviderByCodexIdLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
import {
  useGetFileUrlFromStorageMutation,
  useGetSignUrlFromStorageForRequesterLazyQuery,
} from 'graphql/generated/hasura';
import { useGetPatientImage } from 'hooks/useGetPatientImage';
import { Roles } from '../../../firebase/interfaces';
import { UserData } from 'auth/context/AuthContext';
import ChatMediaModal from './ChatMediaModal';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as Messages } from 'assets/icons/messagesQuestion.svg';

interface ChatComponentProps {
  messages: ChatMessage[];
  locale: ChatMessageLocale;
  loggedUser: UserData | null;
  patientId?: string;
  providerId?: string;
  appointmentId?: string;
}

interface ICollocutor {
  SENSITIVE_firstname: string | null | undefined;
  SENSITIVE_lastname: string | null | undefined;
  senderPicture?: string;
}

export const ChatComponent: React.FC<ChatComponentProps> = ({
  messages,
  locale,
  loggedUser,
  patientId,
  providerId,
  appointmentId,
}) => {
  const { state } = useLocation();
  const [collocutor, setCollocutor] = useState<ICollocutor | null>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>('');
  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation({});

  const patientCodexId = patientId || state?.patientId;
  const providerCodexId = providerId || state?.providerId;

  const [src, setSrc] = useState<string | null | undefined>(null);

  const [getProvider] = useGetFhirProviderByCodexIdLazyQuery({
    onCompleted: (data) => data,
  });

  const [getPatient] = useGetFhirPatientByCodexIdLazyQuery({
    onCompleted: (data) => data,
    onError: (e) => console.error(e),
  });
  const [getFileUrlFromStorageForRequester] =
    useGetSignUrlFromStorageForRequesterLazyQuery();
  const getPatientImage = useGetPatientImage();

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    containerRef.current?.scrollTo({
      top:
        containerRef.current.scrollHeight - containerRef.current.clientHeight,
      behavior: 'instant' as unknown as ScrollBehavior, //https://github.com/Microsoft/TypeScript/issues/28755,
    });
  }, []);

  const getProviderData = useCallback(async () => {
    const { data } = await getProvider({
      variables: { codexProviderId: providerCodexId },
    });
    const provider =
      data?.getFHIRProviderbyCodexIdArgument.providers[0]?.providers;
    let profileImage = undefined;
    if (!!provider && provider?.SENSITIVE_profile_picture_id) {
      profileImage = await getFileUrlFromStorageForRequester({
        variables: {
          fileId: provider.SENSITIVE_profile_picture_id,
          requestedUserId: providerCodexId,
        },
      });
    }
    return {
      ...provider,
      senderPicture: profileImage?.data?.GetSignUrlFromStorageForRequester?.url,
    };
  }, [getFileUrlFromStorageForRequester, getProvider, providerCodexId]);

  const getPatientData = useCallback(async () => {
    if (loggedUser?.role === Roles.PATIENT) {
      const users = await getPatient({
        variables: { patientCodexId: patientCodexId },
      });
      const user = users.data?.getFHIRPatientbyCodexIDParameter?.users[0];
      let profileImage = undefined;
      if (!!user && user?.SENSITIVE_profile_picture_id) {
        profileImage = await getPatientImage(
          patientCodexId,
          user?.SENSITIVE_profile_picture_id,
        );
      }
      return {
        ...user,
        senderPicture: profileImage,
      };
    }
  }, [getPatient, getPatientImage, loggedUser?.role, patientCodexId]);

  const getProfilePicture = useCallback(
    async (profilePicture: string | undefined) => {
      if (!profilePicture) {
        setProfilePicture(null);
        return;
      }
      const image = await getFileUrlFromStorage({
        variables: {
          fileId: profilePicture,
        },
      });
      setProfilePicture(image.data?.GetSignUrlFormStorage?.url || null);
    },
    [getFileUrlFromStorage],
  );

  const getCollocutor =
    loggedUser?.role === Roles.PATIENT ? getProviderData : getPatientData;

  useEffect(() => {
    getCollocutor()
      .then((data) => {
        const { senderPicture, SENSITIVE_firstname, SENSITIVE_lastname } =
          data ?? {};
        setCollocutor({
          SENSITIVE_firstname,
          SENSITIVE_lastname,
          senderPicture,
        });
      })
      .catch(console.error);
    getProfilePicture(loggedUser?.profilePicture);
  }, [
    getPatientData,
    getProviderData,
    getCollocutor,
    getProfilePicture,
    loggedUser?.profilePicture,
  ]);

  useEffect(() => {
    messages.length > 0 && scrollToBottom();
  }, [messages, scrollToBottom]);
  return (
    <div
      ref={containerRef}
      className="flex flex-col-reverse pr-2 gap-4 w-full max-w-full h-full max-h-full overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
    >
      <div
        className={twMerge(
          'flex flex-col items-center gap-2.5 my-auto',
          messages.length && 'hidden',
        )}
      >
        <Messages className="w-9 h-9 max-w-[2.25rem] fill-med-gray-3" />
        <p className="text-center text-med-gray-3 font-medium">
          {'No messages yet'}
        </p>
      </div>
      {messages &&
        messages.map((message) => {
          const isMyMessage = message.senderType === loggedUser?.role;
          const senderPicture = isMyMessage
            ? profilePicture
            : collocutor?.senderPicture;

          const senderName = isMyMessage
            ? message.senderName
            : collocutor?.SENSITIVE_firstname;

          return (
            <ChatMessageComponent
              id={message.id}
              locale={locale}
              key={message.id}
              mediaURL={message.mediaURL}
              message={message.message}
              messageGroup={message.messageGroup}
              senderPicture={senderPicture}
              senderType={message.senderType}
              senderName={senderName}
              type={message.type}
              setSrc={setSrc}
              appointmentId={appointmentId}
            />
          );
        })}
      <ChatMediaModal src={src} setSrc={setSrc} />
    </div>
  );
};
